/**
 * BENCHMARK-DASHBOARD-INFO.MODAL
 * User-facing explanation of the benchmark comparison panel
 */

import { useTranslation } from "react-i18next"
import Modal from "@/components/modal"
import Button from "@/components/button"
import Chip from "@/components/chip"
import { faArrowLeft, faArrowDown, faArrowUp, faEquals } from "@fortawesome/free-solid-svg-icons"

interface Props {
  onClose: () => void
  domain?: "question" | "nps"
}

const GREEN = "#20CA7E"
const RED = "#eb5a46"
const GREY = "#9CA3AF"

const MINI_BARS_QUESTION = [
  { label: "4–5",  pct: 5  },
  { label: "5–6",  pct: 8  },
  { label: "6–7",  pct: 20 },
  { label: "7–8",  pct: 38 },
  { label: "8–9",  pct: 24 },
  { label: "9–10", pct: 10 },
]
// Single boundary value per bar (like the real histogram's axis ticks) rather than a "a – b"
// range — ranges wrap onto two lines at this column width once negative numbers are involved.
const MINI_BARS_NPS = [
  { label: "-40", pct: 5  },
  { label: "-20", pct: 8  },
  { label: "0",   pct: 20 },
  { label: "20",  pct: 38 },
  { label: "40",  pct: 24 },
  { label: "60",  pct: 10 },
]
const VOUS_IDX = 3
const AVG_IDX  = 2

function MiniHistogram({ domain }: { domain: "question" | "nps" }) {
  const { t } = useTranslation()
  const MINI_BARS = domain === "nps" ? MINI_BARS_NPS : MINI_BARS_QUESTION
  const maxPct = Math.max(...MINI_BARS.map((b) => b.pct))
  const CHART_H = 80

  return (
    <div style={{ width: 260 }}>
      <div className="relative" style={{ height: CHART_H + 32 }}>
        {/* Vous solid line — starts below label (top 36) */}
        <div
          style={{
            position: "absolute",
            left: `${((VOUS_IDX + 0.5) / MINI_BARS.length) * 100}%`,
            top: 36,
            bottom: 4,
            borderLeft: `2px solid ${GREEN}`,
            transform: "translateX(-50%)"
          }}
        />
        {/* Moy. dashed line — starts below label (top 36) */}
        <div
          style={{
            position: "absolute",
            left: `${((AVG_IDX + 0.5) / MINI_BARS.length) * 100}%`,
            top: 36,
            bottom: 4,
            borderLeft: `1.5px dashed ${GREY}`,
            transform: "translateX(-50%)"
          }}
        />

        {/* Labels at top of markers */}
        <span
          className="absolute text-sm font-semibold"
          style={{
            left: `${((VOUS_IDX + 0.5) / MINI_BARS.length) * 100}%`,
            top: 2,
            transform: "translateX(-50%)",
            color: GREEN,
            whiteSpace: "nowrap"
          }}>
          {t("benchmark_q_hist_you")}
        </span>
        <span
          className="absolute text-sm"
          style={{
            left: `${((AVG_IDX + 0.5) / MINI_BARS.length) * 100}%`,
            top: 18,
            transform: "translateX(-50%)",
            color: GREY,
            whiteSpace: "nowrap"
          }}>
          {t("benchmark_q_hist_avg")}
        </span>

        {/* Bars */}
        <div className="absolute bottom-0 left-0 right-0 flex items-end gap-0.5">
          {MINI_BARS.map((bar) => (
            <div
              key={bar.label}
              className="flex-1 rounded-t"
              style={{ height: Math.round((bar.pct / maxPct) * CHART_H), backgroundColor: "#D1D5DB" }}
            />
          ))}
        </div>
      </div>

      {/* Bottom axis */}
      <div style={{ height: 1, backgroundColor: "#E8E8E8", marginBottom: 4 }} />

      {/* X labels */}
      <div className="flex">
        {MINI_BARS.map((bar) => (
          <div key={bar.label} className="flex-1 text-center">
            <span className="text-sm grey-t">{bar.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface SectionProps {
  num: number
  color: string
  title: string
  children: React.ReactNode
}

function Section({ num, color, title, children }: SectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: color }}>
          {num}
        </div>
        <span className="font-semibold">{title}</span>
      </div>
      {children}
    </div>
  )
}

export default function BenchmarkDashboardInfoModal({ onClose, domain = "question" }: Props) {
  const { t } = useTranslation()
  const isNps = domain === "nps"

  return (
    <Modal
      isFullScreen
      onClose={onClose}
      title={t("benchmark_q_info_title")}
      footer={
        <Button isWithBorder icon={faArrowLeft} onClick={onClose}>
          {t("utils_back")}
        </Button>
      }>
      <div className="flex flex-col gap-8 py-2">
        <p className="grey-t">{t(isNps ? "benchmark_nps_info_intro" : "benchmark_q_info_intro")}</p>

        <Section num={1} color="#6366f1" title={t("benchmark_q_info_s1_title")}>
          <p className="grey-t">{t("benchmark_q_info_s1_desc")}</p>
          <div className="flex justify-around rounded-xl border border-gray-100 bg-gray-50 px-6 py-5">
            {([
              {
                icon: faArrowDown,
                color: RED,
                label: t("benchmark_q_step_below_avg"),
                range: t(isNps ? "benchmark_nps_range_below_avg" : "benchmark_q_range_below_avg")
              },
              {
                icon: faEquals,
                color: GREY,
                label: t("benchmark_q_step_average"),
                range: t(isNps ? "benchmark_nps_range_average" : "benchmark_q_range_average")
              },
              {
                icon: faArrowUp,
                color: GREEN,
                label: t("benchmark_q_step_above_avg"),
                range: t(isNps ? "benchmark_nps_range_above_avg" : "benchmark_q_range_above_avg")
              },
            ]).map(({ icon, color, label, range }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <Chip icon={icon} color={color}>{label}</Chip>
                <span className="text-sm grey-t text-center tabular-nums leading-tight" style={{ maxWidth: 120 }}>{range}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section num={2} color="#0ea5e9" title={t("benchmark_q_info_s3_title")}>
          <p className="grey-t">{t("benchmark_q_info_s3_desc")}</p>
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-6 py-5 flex items-start gap-8">
            <MiniHistogram domain={domain} />
            <div className="flex flex-col gap-3 grey-t mt-4">
              <div className="flex items-center gap-2">
                <div style={{ width: 22, height: 10, backgroundColor: "#D1D5DB", borderRadius: 3 }} />
                <span className="text-sm">{t("benchmark_q_legend_companies_dist")}</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 22, height: 0, borderTop: `2px solid ${GREEN}` }} />
                <span className="text-sm">{t("benchmark_q_legend_your_score_line")}</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 22, height: 0, borderTop: `1.5px dashed ${GREY}` }} />
                <span className="text-sm">{t("benchmark_q_legend_sector_avg")}</span>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </Modal>
  )
}
